// =============================================================================
// Chart-of-Accounts hierarchy helpers.
//
// QuickBooks Online lets users nest accounts (parent + sub-accounts), and
// when the bookkeeper goes to categorise a transaction inside QBO itself
// the picker:
//
//   1. Indents sub-accounts under their parent so the tree is visible at a
//      glance.
//   2. Disables ("locks") any parent that has children — QBO won't accept a
//      posting against a parent that has sub-accounts; you must pick a leaf.
//
// We mirror that UX in Atlas's three category pickers (Transactions list,
// Transaction detail, Expense detail) via the helpers below. Each picker
// passes its CoA rows in, asks for the per-account-type tree, and renders
// the resulting `FlattenedCoaNode[]` directly with `depth` driving the
// indent and `locked` driving the disabled state.
//
// Why a flattened, pre-ordered list instead of a recursive tree at the
// callsite? Radix Select doesn't support nested SelectItems — every item
// must be a direct child of a SelectContent / SelectGroup. Flattening here
// keeps the rendering code in the page components trivial (`items.map(...)`)
// without losing the hierarchy.
// =============================================================================
import type { Tables } from '@/types/database';

export type CoaRow = Tables<'chart_of_accounts'>;

export type CoaForPicker = Pick<
  CoaRow,
  'id' | 'name' | 'code' | 'account_type' | 'parent_id' | 'is_active'
>;

export type FlattenedCoaNode = {
  account: CoaForPicker;
  /** Tree depth — 0 for top-level, 1 for first-level children, etc. */
  depth: number;
  /**
   * True when this account has at least one child. Parents are "locked"
   * for posting in QBO, so we surface them in the picker as disabled
   * headers (visible, greyed) rather than selectable rows.
   */
  locked: boolean;
};

// Render order for QB account_type groups inside the Category Select. We
// front-load Expenses since the spend pages are the primary callsite.
export const QB_GROUP_ORDER: Array<{ key: string; label: string }> = [
  { key: 'expense', label: 'Expenses' },
  { key: 'asset', label: 'Assets' },
  { key: 'liability', label: 'Liabilities' },
  { key: 'equity', label: 'Equity' },
  { key: 'revenue', label: 'Revenue' },
];

/** "1100 · Operating Cash" if a code is present, else just the name. */
export function formatQbAccountLabel(
  acct: Pick<CoaRow, 'code' | 'name'>,
): string {
  return acct.code ? `${acct.code} · ${acct.name}` : acct.name;
}

/**
 * Maps an assigned Category + Atlas transaction direction to the QBO entity
 * the qbo-post-transaction Edge Function will emit. Mirrors the classifier
 * in supabase/functions/qbo-post-transaction/index.ts so the UI can show
 * the form an operator will see in QBO before they click the button.
 *
 *   account_type = 'expense' | 'cost' + direction='debit'      → Purchase
 *   account_type = 'expense' | 'cost' + direction='credit'
 *                                  + bank-side CC              → CreditCardCredit
 *                                                               ("Credit Card Credit")
 *   account_type = 'expense' | 'cost' + direction='credit'
 *                                  + bank-side cash            → Deposit
 *                                                               (vendor refund check
 *                                                               line-account = expense)
 *   account_type = 'revenue' | 'income'                        → Deposit
 *   account_type = 'equity'  + direction='credit'              → Deposit
 *   account_type = 'equity'  + direction='debit'               → Purchase
 *   account_type = 'liability' + direction='credit'            → Deposit (loan
 *                                                               disbursement, LOC
 *                                                               draw, owner loan in)
 *   account_type = 'liability' (CC subtype) + direction='debit'
 *                              + bank-side cash                → CreditCardPaymentTxn
 *   account_type = 'liability' (anything else, debit)          → Transfer
 *   account_type = 'asset'                                     → Transfer
 *
 * `CreditCardCredit` is a synthetic kind: in QBO it's the same Purchase
 * entity with `Credit: true` (and PaymentType='CreditCard'), so on the
 * server side `qb_external_type` is plain 'Purchase'. The UI keeps it
 * distinct here so the "Will post as …" preview can read "Credit Card
 * Credit" — what the operator will actually see in QBO's interface.
 *
 * The bank-side check is optional — when the caller doesn't have a bank
 * account in scope (e.g. the picker is just previewing how a CoA row
 * would classify in the abstract) we assume the source is cash, which is
 * the dominant case. The server is authoritative and re-classifies with
 * the full bank join in hand.
 *
 * Returns null when there's nothing to classify (no Category yet, or an
 * unrecognised account_type) so the caller can hide the hint instead of
 * showing a misleading placeholder.
 */
export type QboEntityKind =
  | 'Purchase'
  | 'Deposit'
  | 'Transfer'
  | 'CreditCardPaymentTxn'
  | 'CreditCardCredit';

export function classifyQboEntityKind(
  account: Pick<CoaRow, 'account_type' | 'subtype'> | null | undefined,
  direction: 'debit' | 'credit',
  bankAccount?: { account_type: string | null } | null,
): QboEntityKind | null {
  if (!account) return null;
  const at = (account.account_type ?? '').toLowerCase();
  const isBankSideCC = bankAccount
    ? (bankAccount.account_type ?? '').toLowerCase().includes('credit')
    : false;
  if (at === 'expense' || at === 'cost') {
    if (direction === 'credit') {
      // Refund. If we have a bank account in context we can branch
      // precisely; without one we fall back to the more common cash-bank
      // case and let the server re-classify.
      return isBankSideCC ? 'CreditCardCredit' : 'Deposit';
    }
    return 'Purchase';
  }
  if (at === 'revenue' || at === 'income') return 'Deposit';
  if (at === 'equity') return direction === 'credit' ? 'Deposit' : 'Purchase';
  if (at === 'liability') {
    // Loan disbursement / LOC draw / owner loan into bank — money in
    // against a liability looks bookkeeping-identical to an income
    // deposit and uses QBO's Deposit entity with the liability as the
    // line account.
    if (direction === 'credit') return 'Deposit';
    // CC payoff requires money flowing FROM a cash account INTO a
    // credit-card liability. If we have a bank account in context, we
    // can reject CC↔CC hops; otherwise assume cash on the source side.
    const isBankSideCash = bankAccount ? !isBankSideCC : true;
    const sub = (account.subtype ?? '').toLowerCase().replace(/[\s_-]/g, '');
    if (isBankSideCash && sub === 'creditcard') {
      return 'CreditCardPaymentTxn';
    }
    return 'Transfer';
  }
  if (at === 'asset') return 'Transfer';
  return null;
}

/** Short human description of what a given kind means in QBO. */
export function describeQboEntityKind(kind: QboEntityKind): string {
  switch (kind) {
    case 'Purchase':
      return 'an expense';
    case 'Deposit':
      return 'a deposit';
    case 'Transfer':
      return 'a transfer';
    case 'CreditCardPaymentTxn':
      return 'a credit-card payment';
    case 'CreditCardCredit':
      return 'a credit-card refund';
  }
}

/**
 * The label QBO shows for the entity in its UI. We surface this on the
 * "Will post as …" preview so operators recognise the QBO form they'll
 * see when they click through — `CreditCardPaymentTxn` reads as
 * "Pay down credit card" in QBO, not "Credit Card Payment Txn", and
 * `CreditCardCredit` (technically a Purchase{Credit=true}) reads as
 * "Credit Card Credit".
 */
export function qboEntityKindLabel(kind: QboEntityKind): string {
  switch (kind) {
    case 'Purchase':
      return 'Purchase';
    case 'Deposit':
      return 'Deposit';
    case 'Transfer':
      return 'Transfer';
    case 'CreditCardPaymentTxn':
      return 'Pay down credit card';
    case 'CreditCardCredit':
      return 'Credit Card Credit';
  }
}

/**
 * Returns the set of CoA ids that have at least one (active) child —
 * these are the "locked" parent accounts QBO won't accept postings
 * against. Used to dedup suggestion lists so we never suggest a
 * non-postable parent.
 */
export function findLockedAccountIds(
  coa: CoaForPicker[] | undefined | null,
): Set<string> {
  const locked = new Set<string>();
  if (!coa) return locked;
  for (const a of coa) {
    if (!a.is_active) continue;
    if (a.parent_id) locked.add(a.parent_id);
  }
  return locked;
}

/**
 * Group active CoA rows by account_type and return each group as a
 * pre-ordered, flattened tree.
 *
 * Sorting at every depth is by code (numerical-ish via localeCompare) then
 * by name — matches the existing flat sort the pickers used before, just
 * applied per-sibling instead of globally so children stay anchored under
 * their parent.
 *
 * An account whose `parent_id` points at a row that's inactive (or in a
 * different account_type, or simply missing) is promoted to the top level
 * of its own group so we never silently drop it from the picker.
 */
export function buildCoaHierarchyByType(
  coa: CoaForPicker[] | undefined | null,
): Map<string, FlattenedCoaNode[]> {
  const grouped = new Map<string, FlattenedCoaNode[]>();
  if (!coa || coa.length === 0) return grouped;

  const activeRows = coa.filter((a) => a.is_active);
  const byType = new Map<string, CoaForPicker[]>();
  for (const a of activeRows) {
    const arr = byType.get(a.account_type) ?? [];
    arr.push(a);
    byType.set(a.account_type, arr);
  }

  for (const [type, rows] of byType) {
    const idSet = new Set(rows.map((r) => r.id));
    const childrenByParent = new Map<string | null, CoaForPicker[]>();
    for (const r of rows) {
      // Treat "parent in a different (or inactive) bucket" the same as
      // "no parent" — better to surface the row than hide it under a
      // node that won't render in this group.
      const effectiveParent =
        r.parent_id && idSet.has(r.parent_id) ? r.parent_id : null;
      const arr = childrenByParent.get(effectiveParent) ?? [];
      arr.push(r);
      childrenByParent.set(effectiveParent, arr);
    }

    const sortSiblings = (arr: CoaForPicker[]) =>
      arr.sort((x, y) => {
        const xc = x.code ?? '';
        const yc = y.code ?? '';
        if (xc && yc && xc !== yc) return xc.localeCompare(yc);
        return x.name.localeCompare(y.name);
      });
    for (const arr of childrenByParent.values()) sortSiblings(arr);

    const flat: FlattenedCoaNode[] = [];
    const visit = (parentId: string | null, depth: number) => {
      const kids = childrenByParent.get(parentId) ?? [];
      for (const k of kids) {
        const hasChildren = (childrenByParent.get(k.id) ?? []).length > 0;
        flat.push({ account: k, depth, locked: hasChildren });
        if (hasChildren) visit(k.id, depth + 1);
      }
    };
    visit(null, 0);

    grouped.set(type, flat);
  }

  return grouped;
}
